import {
  Box,
  Card,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { blue, green, orange, purple } from '@mui/material/colors';
import React, { useEffect } from 'react';
import { MdAdd, MdAssignment, MdCheck, MdList, MdVisibility } from 'react-icons/md';
import { Outlet, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import AssignedDocumentsList from './AssignedDocumentsList';
import AssignTemplatePage from './AssignTemplatePage';
import CreateDocumentPage from './CreateDocumentPage';
import { useDocumentManagement } from './DocumentManagementContext';
import TemplateList from './ViewTemplatesListComponent';

const DocumentManagement: React.FC = () => {
  const navigate = useNavigate();
  const { activeView, setActiveView, isAdminLogin } = useDocumentManagement();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // Sync activeView with current route on mount
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/payroll/documents/assigned') || path === '/payroll/documents') {
      setActiveView('assigned');
    } else if (path.includes('/payroll/documents/templates')) {
      setActiveView('templates');
    } else if (path.includes('/payroll/documents/assign')) {
      setActiveView('assign');
    } else if (path.includes('/payroll/documents/create')) {
      setActiveView('create');
    }
  }, [location.pathname, setActiveView]);

  const handleCardClick = (view: string, path: string) => {
    setActiveView(view);
    navigate(path);
  };

  // Base action buttons - all available options
  const allActionButtons = [
    {
      id: 'assigned',
      title: 'Assigned Templates',
      description: 'View assigned templates for compliance',
      shortDesc: 'View assigned',
      icon: <MdCheck />,
      color: purple[500],
      action: () => handleCardClick('assigned', '/payroll/documents/assigned'),
      accessible: true // Always accessible to all users
    },
    {
      id: 'templates',
      title: 'View Templates',
      description: 'Browse and manage existing templates',
      shortDesc: 'Browse templates',
      icon: <MdVisibility />,
      color: green[500],
      action: () => handleCardClick('templates', '/payroll/documents/templates'),
      accessible: isAdminLogin // Only accessible to admins
    },
    {
      id: 'assign',
      title: 'Assign Templates',
      description: 'Assign templates to employees',
      shortDesc: 'Assign to users',
      icon: <MdAssignment />,
      color: orange[500],
      action: () => handleCardClick('assign', '/payroll/documents/assign'),
      accessible: isAdminLogin // Only accessible to admins
    },
    {
      id: 'create',
      title: 'Create Template',
      description: 'Create new document templates with required fields',
      shortDesc: 'Create new',
      icon: <MdAdd />,
      color: blue[500],
      action: () => handleCardClick('create', '/payroll/documents/create'),
      accessible: isAdminLogin // Only accessible to admins
    }
  ];

  // Filter buttons based on user role and permissions
  const accessibleButtons = allActionButtons.filter((button) => button.accessible);

  // Auto-redirect non-admin users to assigned view if they're on an unauthorized route
  useEffect(() => {
    if (!isAdminLogin && activeView !== 'assigned') {
      setActiveView('assigned');
      navigate('/payroll/documents/assigned');
    }
  }, [isAdminLogin, activeView, setActiveView, navigate]);

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 0.5, md: 1 }, py: { xs: 0.5, md: 1 } }}>
      {/* Header - Dynamic based on user role */}
      <Box sx={{ mb: { xs: 1.5, md: 2 } }}>
        <Typography
          variant={isMobile ? 'subtitle1' : 'h5'}
          sx={{ fontWeight: 'bold', color: 'primary.main', mb: 0.5 }}
        >
          Document Management
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}
        >
          {isAdminLogin
            ? 'Admin: Manage document assignments across all users'
            : 'Employee: View and manage your assigned documents'}
        </Typography>
      </Box>

      {/* Action Cards - Only show if admin has multiple options or always show for context */}
      {isAdminLogin && (
        <Box sx={{ mb: { xs: 1.5, md: 2 } }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <MdList size={isMobile ? 16 : 20} color={purple[500]} />
            <Typography
              variant={isMobile ? 'body2' : 'subtitle1'}
              sx={{
                fontWeight: 600,
                fontSize: isMobile ? '0.9rem' : '1rem'
              }}
            >
              Template Actions
            </Typography>
            <Chip
              label={`${accessibleButtons.length} Available`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: `${purple[500]}15`,
                color: purple[500]
              }}
            />
          </Stack>

          <Grid container spacing={isMobile ? 1 : 1.5}>
            {accessibleButtons.map((button, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Card
                  sx={{
                    p: isMobile ? 1 : 1.5,
                    borderRadius: 1.5,
                    boxShadow: activeView === button.id ? 3 : 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    border:
                      activeView === button.id
                        ? `2px solid ${button.color}`
                        : `1px solid ${theme.palette.divider}`,
                    backgroundColor:
                      activeView === button.id ? `${button.color}08` : 'background.paper',
                    '&:hover': {
                      boxShadow: 2,
                      transform: isMobile ? 'none' : 'translateY(-2px)',
                      backgroundColor: `${button.color}08`,
                      borderColor: button.color
                    },
                    height: isMobile ? 90 : 120,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    position: 'relative'
                  }}
                  onClick={button.action}
                >
                  {/* Active Indicator */}
                  {activeView === button.id && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: button.color
                      }}
                    />
                  )}

                  {/* Icon */}
                  <Box
                    sx={{
                      width: isMobile ? 32 : 40,
                      height: isMobile ? 32 : 40,
                      borderRadius: 1,
                      bgcolor: activeView === button.id ? `${button.color}20` : `${button.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 0.75,
                      color: button.color
                    }}
                  >
                    {React.cloneElement(button.icon, { size: isMobile ? 16 : 20 })}
                  </Box>

                  {/* Title */}
                  <Typography
                    variant={isMobile ? 'caption' : 'body2'}
                    fontWeight={600}
                    sx={{
                      mb: 0.25,
                      fontSize: isMobile ? '0.7rem' : '0.8rem',
                      lineHeight: 1.2
                    }}
                  >
                    {isMobile ? button.title.split(' ')[0] : button.title}
                  </Typography>

                  {/* Description - Only on larger screens */}
                  {!isMobile && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.65rem',
                        lineHeight: 1.3,
                        textAlign: 'center'
                      }}
                    >
                      {isTablet ? button.shortDesc : button.description}
                    </Typography>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Divider - Only show for admins with multiple options */}
      {isAdminLogin && <Divider sx={{ my: { xs: 1.5, md: 2 }, opacity: 0.6 }} />}

      {/* Routes Content */}
      <Box sx={{ minHeight: 'calc(100vh - 200px)' }}>
        <Routes>
          {/* Default route - always show assigned documents */}
          <Route index element={<AssignedDocumentsList />} />

          {/* Assigned route - accessible to all users */}
          <Route path="assigned" element={<AssignedDocumentsList />} />

          {/* Admin-only routes */}
          {isAdminLogin && (
            <>
              <Route path="templates" element={<TemplateList />} />
              <Route path="create" element={<CreateDocumentPage />} />
              <Route path="assign" element={<AssignTemplatePage />} />
            </>
          )}

          {/* Fallback for unauthorized routes - redirect to assigned */}
          <Route path="*" element={<AssignedDocumentsList />} />
        </Routes>
        <Outlet />
      </Box>
    </Container>
  );
};

export default DocumentManagement;
